import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import VendorList from './VendorList';
import VendorForm from './VendorForm';
import VendorDetails from './VendorDetails';
import BulkUploadModal from './BulkUploadModal';
import { Vendor, VendorSummary, PaginationInfo } from './types';
import config from '../../config';

const VendorManagement: React.FC = () => {
  const [vendors, setVendors] = useState<VendorSummary[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);

  const baseURL = config.API_BASE_URL;

  // Fetch vendors
  const fetchVendors = useCallback(async (page = 1, search = '') => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        setError('No authentication token found. Please log in again.');
        setLoading(false);
        return;
      }
      
      console.log('Making vendor API request with token:', token ? 'Token present' : 'No token');
      
      const response = await axios.get(`${baseURL}/vendors`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page,
          limit: 10,
          search: search.trim() || undefined
        }
      });

      setVendors(response.data.data);
      setPagination(response.data.pagination);
    } catch (err: any) {
      if (err.response?.status === 401) {
        setError('Authentication failed. Please log out and log in again.');
      } else {
        setError(err.response?.data?.message || 'Failed to fetch vendors');
      }
      console.error('Error fetching vendors:', err);
    } finally {
      setLoading(false);
    }
  }, [baseURL]);

  // Delete vendor
  const handleDeleteVendor = async (vendorId: string) => {
    if (!window.confirm('Are you sure you want to delete this vendor? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(`${baseURL}/vendors/${vendorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('Vendor deleted successfully');
      fetchVendors(pagination.currentPage, searchTerm);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete vendor');
      console.error('Error deleting vendor:', err);
    }
  };

  // Handle vendor creation/update
  const handleVendorSaved = () => {
    setShowAddForm(false);
    setShowEditForm(false);
    setSelectedVendor(null);
    fetchVendors(pagination.currentPage, searchTerm);
  };

  // Handle search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchVendors(1, searchTerm);
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    fetchVendors(page, searchTerm);
  };

  // Handle bulk upload success
  const handleBulkUploadSuccess = () => {
    setShowBulkUpload(false);
    fetchVendors(pagination.currentPage, searchTerm);
  };

  // View vendor details
  const handleViewDetails = async (vendorSummary: VendorSummary) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${baseURL}/vendors/${vendorSummary._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedVendor(response.data.data);
      setShowDetails(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch vendor details');
    }
  };

  // Edit vendor
  const handleEditVendor = async (vendorSummary: VendorSummary) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${baseURL}/vendors/${vendorSummary._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedVendor(response.data.data);
      setShowEditForm(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch vendor details');
    }
  };

  // Clear messages
  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  // Auto-clear messages after 5 seconds
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(clearMessages, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  return (
    <div className="vendor-management">
      {/* Action Buttons */}
      <div className="vendor-actions mb-3">
        <button 
          className="btn btn-primary me-2"
          onClick={() => setShowAddForm(true)}
        >
          <i className="bi bi-plus-circle me-1"></i>
          Add Vendor
        </button>
        <button 
          className="btn btn-secondary"
          onClick={() => setShowBulkUpload(true)}
        >
          <i className="bi bi-upload me-1"></i>
          Bulk Upload
        </button>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="search-form">
        <div className="search-group">
          <input
            type="text"
            placeholder="Search vendors by name or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="btn btn-search">
            🔍 Search
          </button>
          {searchTerm && (
            <button 
              type="button" 
              className="btn btn-clear"
              onClick={() => {
                setSearchTerm('');
                fetchVendors(1, '');
              }}
            >
              ✕ Clear
            </button>
          )}
        </div>
      </form>

      {/* Messages */}
      {error && (
        <div className="alert alert-error">
          {error}
          <button onClick={clearMessages} className="alert-close">✕</button>
        </div>
      )}
      {success && (
        <div className="alert alert-success">
          {success}
          <button onClick={clearMessages} className="alert-close">✕</button>
        </div>
      )}

      {/* Vendor List */}
      <VendorList
        vendors={vendors}
        loading={loading}
        pagination={pagination}
        onPageChange={handlePageChange}
        onViewDetails={handleViewDetails}
        onEditVendor={handleEditVendor}
        onDeleteVendor={handleDeleteVendor}
      />

      {/* Modals */}
      {showAddForm && (
        <VendorForm
          vendor={null}
          onSave={handleVendorSaved}
          onCancel={() => setShowAddForm(false)}
        />
      )}

      {showEditForm && selectedVendor && (
        <VendorForm
          vendor={selectedVendor}
          onSave={handleVendorSaved}
          onCancel={() => {
            setShowEditForm(false);
            setSelectedVendor(null);
          }}
        />
      )}

      {showDetails && selectedVendor && (
        <VendorDetails
          vendor={selectedVendor}
          onClose={() => {
            setShowDetails(false);
            setSelectedVendor(null);
          }}
          onEdit={() => {
            setShowDetails(false);
            setShowEditForm(true);
          }}
          onRefresh={() => fetchVendors(pagination.currentPage, searchTerm)}
        />
      )}

      {showBulkUpload && (
        <BulkUploadModal
          onSuccess={handleBulkUploadSuccess}
          onClose={() => setShowBulkUpload(false)}
        />
      )}
    </div>
  );
};

export default VendorManagement;
